import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { Breadcrumb, Button, If } from '@/lib/v2/components';
import { ModalSingleInput } from '@/lib/v2/organisms';

import {
  atomActionNodeInfo,
  atomCallbackActivateRule,
  atomCallbackPauseRule,
  atomCallbackSaveRule,
  atomDisableEditRule,
  atomIsLoadingRuleData,
  atomIsTriggerFormComplete,
  atomRuleDataRaw,
  atomToggleActivateRuleModal,
} from '@/src/modules/RulesModule/atoms/rules';
import {
  TRANSLATE_RULES_CALL_TO_ACTION,
  TRANSLATE_RULES_RENAME,
} from '@/src/modules/RulesModule/constants';
import { useRuleStatus } from '@/src/modules/RulesModule/hooks/useRuleStatus';
import { RuleTypes } from '@/src/modules/RulesModule/interfaces';

import BackToRulesModal from './components/BackToRulesModal';

import { AUTOMATION_PATHS, JOURNEY_BACK_URL_KEY } from '@/modules/AutomationModuleNew/constants';
import { useNavigateAndBack } from '@/modules/AutomationModuleNew/hooks/useNavigateAndBack';
import {
  atomActionInfoEmail,
  atomProcessingAction,
} from '@/modules/CampaignsModule/atoms/actionEmailAtom';
import { atomToggleEditActionsModal } from '@/modules/CampaignsModule/atoms/modalsCampaignAtom';
import { CAMPAIGNS_PATH } from '@/modules/CampaignsModule/constants';
import { useActionData } from '@/modules/CampaignsModule/hooks/useActionData';
import { useCampaignLastPath } from '@/modules/CampaignsModule/hooks/useCampaignLastPath';
import { useEditActions } from '@/modules/CampaignsModule/hooks/useEditActions';
import { isActionEditable } from '@/modules/CampaignsModule/utils';
import { ActivateAction } from '@/modules/RulesModule/components/ActivateAction';

const HeaderSectionRules = () => {
  const { t } = useTranslation('rules');
  const { t: tn } = useTranslation();
  const navigate = useNavigate();
  const { campaignId, actionId } = useParams();
  const { pathname } = useLocation();

  const [toggleBackToRulesModal, setToggleBackToRulesModal] = useState<boolean>(false);

  const setToggleActivateRuleModal = useSetAtom(atomToggleActivateRuleModal);
  const setDisableEditRule = useSetAtom(atomDisableEditRule);
  const callbackSaveRule = useAtomValue(atomCallbackSaveRule);
  const callbackActivateRule = useAtomValue(atomCallbackActivateRule);
  const callbackPauseRule = useAtomValue(atomCallbackPauseRule);
  const isTriggerFormComplete = useAtomValue(atomIsTriggerFormComplete);
  const actionNodeInfo = useAtomValue(atomActionNodeInfo);
  const actionInfoEmail = useAtomValue(atomActionInfoEmail);
  const processingAction = useAtomValue(atomProcessingAction);
  const isLoadingRuleData = useAtomValue(atomIsLoadingRuleData);
  const [toggleEditActionsModal, setToggleEditActionsModal] = useAtom(atomToggleEditActionsModal);
  const rulesDataRaw = useAtomValue(atomRuleDataRaw);

  const isAutomation = pathname.includes('automation');
  const isJourneysPath = pathname.includes('automation/journeys');
  const isRulesPath = pathname.includes('automation/rules');
  const isEmailPath = pathname.endsWith('/email');

  const { action, refetch } = useActionData(Number(actionId));
  const { isRuleActive } = useRuleStatus();

  const {
    state: { VALIDATION_RULES, loading },
    events: { handleApplyRename, handleCloseModal, handleValidation },
  } = useEditActions({ campaignId, actionId });

  useEffect(() => {
    //TODO: Review this refetch & atom for update data
    /* refetch(); */
    setDisableEditRule(!isActionEditable(Number(action?.status)));
  }, [action?.status, actionId, refetch, setDisableEditRule]);

  const lastParam = useMemo(() => {
    const pathSegments = pathname.split('/').filter(Boolean);
    return pathSegments[pathSegments.length - 1];
  }, [pathname]);

  const ruleEmailPath = useMemo(() => lastParam === 'email', [lastParam]);

  /* cspell:disable*/
  const isFlowTemplate = useMemo(() => {
    return rulesDataRaw?.desencadenante === RuleTypes.FLOW_TEMPLATE;
  }, [rulesDataRaw]);
  /* cspell:enable*/

  const handleOpenModal = useCallback(() => {
    setToggleEditActionsModal(true);
  }, [setToggleEditActionsModal]);

  const handleFinishRule = useCallback(() => {
    if (isJourneysPath) {
      navigate(`/v2/${AUTOMATION_PATHS.MODULE_PATH}/${AUTOMATION_PATHS.JOURNEYS_PATH}`);
      return;
    }
    if (isRulesPath) {
      navigate(pathname.replace('/email', ''));
      return;
    }
    navigate(`${CAMPAIGNS_PATH}/${String(campaignId)}`);
  }, [isJourneysPath, isRulesPath, navigate, campaignId, pathname]);

  const handleCallbackContinue = useCallback(() => {
    callbackSaveRule && void callbackSaveRule();
  }, [callbackSaveRule]);

  const handleCallbackActivate = useCallback(() => {
    callbackActivateRule && void callbackActivateRule();
  }, [callbackActivateRule]);

  const handleCallbackPause = useCallback(() => {
    callbackPauseRule && void callbackPauseRule();
  }, [callbackPauseRule]);

  const { campaignLastPath } = useCampaignLastPath();
  const backToAutomation = `/v2/${AUTOMATION_PATHS.MODULE_PATH}`;
  const backToJourneys = `/v2/${AUTOMATION_PATHS.MODULE_PATH}/${AUTOMATION_PATHS.JOURNEYS_PATH}`;
  const backToRules = `/v2/${AUTOMATION_PATHS.MODULE_PATH}/${AUTOMATION_PATHS.RULES_PATH}`;
  const { hasBackUrl, getBackUrlAndRemoveSS } = useNavigateAndBack(JOURNEY_BACK_URL_KEY);

  /* cspell:disable*/
  const headerRulesPaths = useMemo(() => {
    const breadcrumbLinksJourneys = [
      {
        id: 'automation-path',
        name: tn('BREADCRUMB.automation'),
        to: { path: backToAutomation },
      },
      {
        id: 'journeys-home-path',
        name: tn('BREADCRUMB.journeys'),
        ...(!hasBackUrl && { to: { path: backToJourneys } }),
      },
      {
        id: 'action-rules-path',
        name: action ? action.name : '',
      },
    ];

    const breadcrumbLinksRules = [
      {
        id: 'automation-path',
        name: tn('BREADCRUMB.automation'),
        to: { path: backToAutomation },
      },
      {
        id: 'rules-home-path',
        name: tn('BREADCRUMB.rules'),
        to: { path: backToRules },
      },
      {
        id: 'rule-name-path',
        name: action ? action.name : '',
      },
    ];

    const breadcrumbLinks = [
      {
        id: 'campaign-rules-id-path',
        name: action ? action.campaignName : '',
        to: {
          path: campaignLastPath.action,
        },
      },
      {
        id: 'action-rules-id-path',
        name: action ? action.name : '',
      },
    ];

    if (isAutomation) {
      if (isJourneysPath) {
        return breadcrumbLinksJourneys;
      }
      if (isRulesPath) {
        return breadcrumbLinksRules;
      }
    }

    return breadcrumbLinks;
  }, [
    action,
    backToAutomation,
    backToJourneys,
    backToRules,
    campaignLastPath.action,
    hasBackUrl,
    isAutomation,
    isJourneysPath,
    isRulesPath,
    tn,
  ]);
  /* cspell:enable*/

  const isActivateButtonDisabled = useMemo(() => {
    return (
      !isTriggerFormComplete || !actionNodeInfo.complete || !actionInfoEmail?.emailInfo.subject
    );
  }, [actionNodeInfo.complete, actionInfoEmail?.emailInfo.subject, isTriggerFormComplete]);

  const headerRulesActionButtons = useMemo(
    () => (
      <div className="flex gap-3">
        <If condition={!ruleEmailPath || (isJourneysPath && ruleEmailPath) || isFlowTemplate}>
          <If condition={!isRuleActive}>
            <Button outline id="header-action__save" size="small" onClick={handleFinishRule}>
              {t(`${TRANSLATE_RULES_CALL_TO_ACTION}.finish`)}
            </Button>
          </If>

          <If condition={isRuleActive}>
            <Button
              primary
              id="header-action__activate"
              isLoading={processingAction || isLoadingRuleData}
              size="small"
              onClick={handleCallbackPause}
            >
              {t(`${TRANSLATE_RULES_CALL_TO_ACTION}.pause`)}
            </Button>
          </If>

          <If condition={!isRuleActive}>
            <Button
              primary
              disabled={!isFlowTemplate ? isActivateButtonDisabled : false}
              id="header-action__activate"
              isLoading={processingAction || isLoadingRuleData}
              size="small"
              onClick={
                isFlowTemplate ? handleCallbackContinue : () => setToggleActivateRuleModal(true)
              }
            >
              {t(`${TRANSLATE_RULES_CALL_TO_ACTION}.activate`)}
            </Button>
          </If>
        </If>

        <If condition={ruleEmailPath && !isRuleActive && !isJourneysPath && !isFlowTemplate}>
          <Button
            primary
            id="header-action__activate"
            isLoading={processingAction}
            size="small"
            onClick={handleCallbackContinue}
          >
            {t(`${TRANSLATE_RULES_CALL_TO_ACTION}.continue`)}
          </Button>
        </If>
      </div>
    ),
    [
      ruleEmailPath,
      isJourneysPath,
      isFlowTemplate,
      isRuleActive,
      handleFinishRule,
      t,
      processingAction,
      isLoadingRuleData,
      handleCallbackPause,
      isActivateButtonDisabled,
      handleCallbackContinue,
      setToggleActivateRuleModal,
    ]
  );

  const handleBackHeader = useCallback(() => {
    if (hasBackUrl) {
      navigate(getBackUrlAndRemoveSS());
      return;
    }
    if (isAutomation) {
      if (isJourneysPath) {
        navigate(backToJourneys);
        return;
      }
      if (isRulesPath) {
        if (isEmailPath) {
          const newPath = pathname.slice(0, pathname.lastIndexOf('/'));
          navigate(newPath);
          return;
        }
        navigate(backToRules);
        return;
      }
    }
    if (ruleEmailPath && !isRuleActive) {
      setToggleBackToRulesModal(true);
      return;
    }

    const locationPath = ruleEmailPath
      ? pathname.split('/').slice(0, -1).join('/')
      : campaignLastPath.action;

    navigate(locationPath);
  }, [
    hasBackUrl,
    isAutomation,
    ruleEmailPath,
    isRuleActive,
    pathname,
    campaignLastPath.action,
    navigate,
    getBackUrlAndRemoveSS,
    isJourneysPath,
    isRulesPath,
    backToJourneys,
    isEmailPath,
    backToRules,
  ]);

  return (
    <div className="relative z-[1]">
      <Breadcrumb
        isEditable
        withoutMargin
        actionButtons={rulesDataRaw ? headerRulesActionButtons : undefined}
        customPaths={headerRulesPaths}
        handleOnBackClick={handleBackHeader}
        itemId={action?.actionId}
        onEditableClick={handleOpenModal}
      />
      <If condition={toggleEditActionsModal}>
        <ModalSingleInput
          disabledUntilOnChange
          id="edit-action-table"
          isLoading={loading}
          isOpen={toggleEditActionsModal}
          rules={VALIDATION_RULES}
          title={t(`${TRANSLATE_RULES_RENAME}.rename`)}
          value={action?.name}
          onClose={handleCloseModal}
          onSubmit={handleApplyRename}
          onValidateInputFetch={handleValidation}
        />
      </If>
      <ActivateAction loading={processingAction} onConfirm={handleCallbackActivate} />

      <BackToRulesModal isOpen={toggleBackToRulesModal} onClose={setToggleBackToRulesModal} />
    </div>
  );
};

export default memo(HeaderSectionRules);
